/**
 * A gaveta do Feedback — spec 0108 (DD-09, DD-13).
 *
 * Uma gaveta só, à direita, com duas abas: Enviar e Meus feedbacks. Abrir um
 * item troca a lista pela conversa, com Voltar: nada se empilha (a 0088
 * limita a duas janelas).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, Upload, X } from 'lucide-react';
import { AbasDaJanela, Carregando, EstadoVazio, Janela, PilulaDaJanela, Selo, Veredito } from '../common/interface';
import { quando } from '../../utils/formato';
import {
  TIPOS, ROTULO_TIPO, ROTULO_STATUS, PAPEL_STATUS, MAX_ANEXOS, ACEITA_NO_SELETOR,
  motivoDaRecusa, tamanhoLegivel, enviarFeedback, lerMeus, abrirMeu, responderMeu,
  type Tipo, type FeedbackResumo, type FeedbackDetalhe,
} from './api';
import { Conversa } from './Conversa';

export type EstadoDaCaptura = { tipo: 'pronta'; blob: Blob } | { tipo: 'falhou' } | { tipo: 'removida' };

interface Props {
  aberta: boolean;
  onFechar: () => void;
  captura: EstadoDaCaptura;
  onCaptura: (c: EstadoDaCaptura) => void;
  /** O caminho da tela no menu, ou a aba do portal (FR-005). */
  tela: string;
  /** Avisa o botão de que as respostas foram lidas. */
  onLeu: () => void;
  abaInicial?: 'enviar' | 'meus';
  /**
   * O chamado que já sabe do que se trata (0113): a nota fiscal do portal abre a gaveta com o
   * tipo, o começo da mensagem e o endereço do item. Sem ele, a gaveta abre em branco.
   */
  rascunho?: { tipo: Tipo; mensagem: string; endereco?: string };
}

type Aba = 'enviar' | 'meus';

/** `abrirLogo`: o nº que acabou de ser enviado, aberto direto na conversa. */
function MeusFeedbacks({ onLeu, abrirLogo, onAbriu }: { onLeu: () => void; abrirLogo?: number | null; onAbriu?: () => void }) {
  const [lista, setLista] = useState<FeedbackResumo[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aberto, setAberto] = useState<FeedbackDetalhe | null>(null);
  const [abrindo, setAbrindo] = useState<number | null>(null);
  const voltarPara = useRef<number | null>(null);

  const carregar = useCallback(async () => {
    setErro(null);
    try {
      setLista((await lerMeus()).itens);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível ler os seus feedbacks.');
    }
  }, []);
  useEffect(() => { void carregar(); }, [carregar]);
  useEffect(() => { if (abrirLogo) { void abrir(abrirLogo); onAbriu?.(); } }, [abrirLogo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ao voltar da conversa, o foco volta ao item que estava aberto.
  useEffect(() => {
    if (aberto || voltarPara.current === null || !lista) return;
    document.getElementById(`fb-item-${voltarPara.current}`)?.focus();
    voltarPara.current = null;
  }, [aberto, lista]);

  const abrir = async (id: number) => {
    setAbrindo(id); setErro(null);
    try {
      setAberto(await abrirMeu(id));
      onLeu();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível abrir o feedback.');
    } finally {
      setAbrindo(null);
    }
  };

  if (aberto) {
    return (
      <div className="fb-painel">
        <button type="button" className="fc-btn-link fb-voltar" onClick={() => { voltarPara.current = aberto.id; setAberto(null); void carregar(); }}>
          <ChevronLeft size={16} aria-hidden="true" /> Meus feedbacks
        </button>
        <header className="fb-cabeca">
          <h3 className="fb-cabeca-titulo">{ROTULO_TIPO[aberto.tipo]} <Selo papel={PAPEL_STATUS[aberto.status]}>{ROTULO_STATUS[aberto.status]}</Selo></h3>
          <p className="fb-ajuda">Nº {aberto.id} · enviado em {quando(aberto.criadoEm)}</p>
        </header>
        <Conversa
          detalhe={aberto}
          modo="autor"
          onResponder={async (texto, _nota, anexos) => {
            const faltaram = await responderMeu(aberto.id, texto, anexos);
            setAberto(await abrirMeu(aberto.id));
            return faltaram;
          }}
        />
      </div>
    );
  }

  if (erro) {
    return (
      <EstadoVazio titulo="Não foi possível ler os seus feedbacks" acao={<button type="button" className="fc-btn fc-btn-secundario" onClick={carregar}>Tentar de novo</button>}>
        {erro}
      </EstadoVazio>
    );
  }
  if (!lista) return <Carregando rotulo="Carregando os seus feedbacks" linhas={3} />;
  if (lista.length === 0) {
    return <EstadoVazio titulo="Nenhum feedback enviado">Encontrou um problema ou tem uma ideia? Use a aba Enviar.</EstadoVazio>;
  }
  return (
    <ul className="fb-lista">
      {lista.map((f) => (
        <li key={f.id}>
          <button type="button" id={`fb-item-${f.id}`} className="fb-item" onClick={() => abrir(f.id)} aria-busy={abrindo === f.id || undefined}>
            <span className="fb-item-topo">
              {ROTULO_TIPO[f.tipo]} <Selo papel={PAPEL_STATUS[f.status]}>{ROTULO_STATUS[f.status]}</Selo>
            </span>
            <time className="fb-item-quando" dateTime={f.criadoEm}>{quando(f.criadoEm)}</time>
            <span className="fb-item-texto">{f.mensagem}</span>
            {(f.naoLidas ?? 0) > 0 && (
              <span className="fb-item-nova">{f.naoLidas === 1 ? '1 resposta nova' : `${f.naoLidas} respostas novas`}</span>
            )}
          </button>
        </li>
      ))}
    </ul>
  );
}

export function GavetaFeedback({ aberta, onFechar, captura, onCaptura, tela, onLeu, abaInicial = 'enviar', rascunho }: Props) {
  const [aba, setAba] = useState<Aba>(abaInicial);
  const [tipo, setTipo] = useState<Tipo>('PROBLEMA');
  const [mensagem, setMensagem] = useState('');
  const [anexos, setAnexos] = useState<File[]>([]);
  const [recusa, setRecusa] = useState<string | null>(null);
  const [tentou, setTentou] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [enviado, setEnviado] = useState<{ id: number; faltaram: number } | null>(null);
  const [arrastando, setArrastando] = useState(false);
  const [abrirLogo, setAbrirLogo] = useState<number | null>(null);
  const seletor = useRef<HTMLInputElement>(null);

  useEffect(() => { if (aberta) { setAba(abaInicial); setAbrirLogo(null); } }, [aberta, abaInicial]);
  useEffect(() => {
    if (aberta && rascunho) { setAba('enviar'); setTipo(rascunho.tipo); setMensagem(rascunho.mensagem); }
  }, [aberta, rascunho]);

  const urlDaCaptura = useMemo(() => (captura.tipo === 'pronta' ? URL.createObjectURL(captura.blob) : null), [captura]);
  useEffect(() => () => { if (urlDaCaptura) URL.revokeObjectURL(urlDaCaptura); }, [urlDaCaptura]);

  const curta = tentou && mensagem.trim().length < 3;
  const dica = TIPOS.find((t) => t.valor === tipo)!.dica;

  const adicionar = (lista: FileList | null) => {
    if (!lista) return;
    const novos = [...anexos];
    const recusados: string[] = [];
    for (const f of Array.from(lista)) {
      const motivo = motivoDaRecusa(f) ?? (novos.length >= MAX_ANEXOS ? `já há ${MAX_ANEXOS} anexos` : null);
      if (motivo) recusados.push(`${f.name}: ${motivo}`); else novos.push(f);
    }
    setAnexos(novos);
    setRecusa(recusados.length ? `Ficaram de fora: ${recusados.join(' · ')}` : null);
    if (seletor.current) seletor.current.value = '';
  };

  const limpar = () => {
    setTipo('PROBLEMA'); setMensagem(''); setAnexos([]); setRecusa(null); setTentou(false); setErro(null); setEnviado(null);
  };

  const enviar = async () => {
    setTentou(true);
    if (mensagem.trim().length < 3) {
      document.getElementById('fb-mensagem')?.focus();
      return;
    }
    setEnviando(true); setErro(null);
    try {
      const r = await enviarFeedback({
        tipo, mensagem: mensagem.trim(), tela,
        endereco: rascunho?.endereco ?? window.location.pathname + window.location.search,
        captura: captura.tipo === 'pronta' ? captura.blob : null,
        anexos,
      });
      setEnviado(r);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível enviar o feedback.');
    } finally {
      setEnviando(false);
    }
  };

  const fechar = () => {
    if (enviado) limpar();
    onFechar();
  };

  const formulario = enviado ? (
    <div className="fb-formulario">
      <Veredito
        papel={enviado.faltaram > 0 ? 'atencao' : 'sucesso'}
        titulo={`Recebido. O seu feedback é o nº ${enviado.id}.`}
        acao={(
          <button type="button" className="fc-btn fc-btn-secundario" onClick={() => { const id = enviado.id; limpar(); setAbrirLogo(id); setAba('meus'); }}>
            {enviado.faltaram > 0 ? 'Anexar de novo…' : `Ver o feedback nº ${enviado.id}`}
          </button>
        )}
      >
        {enviado.faltaram > 0
          ? (enviado.faltaram === 1 ? '1 arquivo não chegou. ' : `${enviado.faltaram} arquivos não chegaram. `)
            + 'O texto foi; anexe de novo na conversa.'
          : 'Quando a equipe responder, o botão Feedback mostra um número.'}
      </Veredito>
    </div>
  ) : (
    <div
      className="fb-formulario"
      // Colar uma imagem (Ctrl+V com a tela copiada) vira anexo: é o gesto de quem quer mostrar.
      onPaste={(e) => { if (e.clipboardData.files.length > 0) { e.preventDefault(); adicionar(e.clipboardData.files); } }}
    >
      <PilulaDaJanela
        nome="fb-tipo"
        rotulo="Tipo"
        largo
        opcoes={TIPOS.map((t) => ({ valor: t.valor, rotulo: t.rotulo }))}
        valor={tipo}
        onMudar={(v) => setTipo(v as Tipo)}
      />

      <label className="fb-campo">
        <span className="fb-rotulo">Mensagem</span>
        <span className="fb-ajuda" id="fb-mensagem-dica">{dica}</span>
        <textarea
          id="fb-mensagem"
          value={mensagem}
          maxLength={5000}
          onChange={(e) => setMensagem(e.target.value)}
          aria-invalid={curta || undefined}
          aria-describedby={curta ? 'fb-mensagem-dica fb-mensagem-erro' : 'fb-mensagem-dica'}
        />
        {curta && <span className="fb-erro" id="fb-mensagem-erro">Escreva pelo menos 3 letras.</span>}
      </label>

      <div className="fb-campo">
        <span className="fb-rotulo">Captura desta tela</span>
        {captura.tipo === 'pronta' && urlDaCaptura ? (
          <>
            <a className="fb-captura" href={urlDaCaptura} target="_blank" rel="noopener noreferrer" aria-label="Ver a captura em tamanho real">
              <img src={urlDaCaptura} alt="Captura da tela que você estava vendo" />
            </a>
            <button type="button" className="fc-btn-link fb-captura-remover" onClick={() => onCaptura({ tipo: 'removida' })}>
              <X size={14} aria-hidden="true" /> Remover a captura
            </button>
            <span className="fb-ajuda">Ajuda a equipe a ver o que você viu. Remova se houver algo que não deve ser compartilhado.</span>
          </>
        ) : (
          <p className="fb-sem-captura">
            {captura.tipo === 'falhou'
              ? 'Não foi possível capturar esta tela. O feedback vai sem ela; se quiser, anexe uma imagem.'
              : 'A captura não vai junto.'}
          </p>
        )}
      </div>

      <div className="fb-campo">
        <span className="fb-rotulo" id="fb-anexos-rotulo">Anexos (opcional)</span>
        <button
          type="button"
          className={`fb-soltar${arrastando ? ' fb-soltar-ativo' : ''}`}
          aria-describedby="fb-anexos-ajuda"
          onClick={() => seletor.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setArrastando(true); }}
          onDragLeave={() => setArrastando(false)}
          onDrop={(e) => { e.preventDefault(); setArrastando(false); adicionar(e.dataTransfer.files); }}
          disabled={anexos.length >= MAX_ANEXOS}
        >
          <Upload size={20} aria-hidden="true" />
          <strong><span className="fb-so-mouse">Arraste arquivos aqui ou clique para escolher</span><span className="fb-so-toque">Escolher arquivos</span></strong>
          <span id="fb-anexos-ajuda">Imagem, PDF, vídeo MP4, planilha, documento ou texto · até 50 MB cada · {anexos.length}/{MAX_ANEXOS}<span className="fb-so-mouse"> · ou cole uma imagem com Ctrl+V</span></span>
        </button>
        <input ref={seletor} type="file" multiple hidden accept={ACEITA_NO_SELETOR} onChange={(e) => adicionar(e.target.files)} aria-labelledby="fb-anexos-rotulo" />
        {anexos.length > 0 && (
          <ul className="fb-arquivos">
            {anexos.map((a, i) => (
              <li key={`${a.name}-${i}`} className="fb-arquivo">
                <span>{a.name}</span>
                <small>{tamanhoLegivel(a.size)}</small>
                <button type="button" className="fc-btn-icone" aria-label={`Tirar ${a.name}`} onClick={() => setAnexos(anexos.filter((_, k) => k !== i))}>
                  <X size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
        {recusa && <span className="fb-erro" role="alert">{recusa}</span>}
      </div>
    </div>
  );

  return (
    <Janela
      aberta={aberta}
      tipo="gaveta"
      titulo="Feedback"
      contexto="Encontrou um problema ou tem uma ideia? A equipe lê e responde por aqui."
      erro={aba === 'enviar' ? erro : null}
      ocupado={enviando}
      onFechar={fechar}
      apoio={aba === 'enviar' && !enviado ? 'Vai junto: esta tela, o endereço e o seu perfil.' : null}
      acoes={aba === 'enviar' && !enviado ? (
        <>
          <button type="button" className="fc-btn fc-btn-secundario" onClick={fechar} disabled={enviando}>Cancelar</button>
          <button type="button" className="fc-btn fc-btn-primario" onClick={enviar} disabled={enviando}>
            {enviando ? 'Enviando…' : 'Enviar'}
          </button>
        </>
      ) : undefined}
    >
      <AbasDaJanela
        rotulo="Feedback"
        valor={aba}
        onMudar={(v) => setAba(v as Aba)}
        abas={[
          { chave: 'enviar', rotulo: 'Enviar', conteudo: () => formulario },
          { chave: 'meus', rotulo: 'Meus feedbacks', conteudo: () => <MeusFeedbacks onLeu={onLeu} abrirLogo={abrirLogo} onAbriu={() => setAbrirLogo(null)} /> },
        ]}
      />
    </Janela>
  );
}
